import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";

export default class extends Controller {
  static targets = [
    "grid",
    "card",
    "orderInput",
    "removeInput",
    "removeButton",
    "mainBadge",
    "newBadge",
    "dragHandle",
    "removalBadge",
    "reorderControls",
    "moveEarlierButton",
    "moveLaterButton",
    "presenceInput"
  ];

  connect() {
    this.sortable = null;
    this.initializeSortable();

    this.gridTarget.sortable = this.sortable;

    this.refreshImageOrderState();
  }

  initializeSortable() {
    try {
      this.sortable = Sortable.create(this.gridTarget, {
        dataIdAttr: "id",
        handle: "[data-preparation-images-target='dragHandle']",
        draggable: "[data-preparation-images-target='card']",
        animation: 0,
        ghostClass: "preparation-photo-card--ghost",
        chosenClass: "preparation-photo-card--chosen",
        dragClass: "preparation-photo-card--dragging",
        onEnd: () => {
          this.refreshImageOrderState();
        }
      });
    } catch (error) {
      console.warn("Preparation images sortable failed to initialize.", error);
      this.sortable = null;
    }
  }

  disconnect() {
    if (this.sortable) {
      this.sortable.destroy();
      this.sortable = null;
    }

    if (this.hasGridTarget) {
      this.gridTarget.sortable = null;
    }
  }

  toggleRemoval(event) {
    event.preventDefault();

    const card = event.currentTarget.closest("[data-preparation-images-target='card']");
    if (!card) return;

    const removeInput = this.removeInputForCard(card);
    if (!removeInput) return;

    removeInput.disabled = !removeInput.disabled;
    this.refreshImageOrderState();
    this.dispatchGridChanged();
  }

  moveEarlier(event) {
    event.preventDefault();

    const control = event.currentTarget;
    const card = control.closest("[data-preparation-images-target='card']");
    if (!card) return;

    this.moveCard(card, "earlier");
  }

  moveLater(event) {
    event.preventDefault();

    const control = event.currentTarget;
    const card = control.closest("[data-preparation-images-target='card']");
    if (!card) return;

    this.moveCard(card, "later");
  }

  refreshImageOrderState() {
    const cards = this.cardTargets;
    const activeCards = cards.filter((card) => !this.isCardRemoved(card));
    const mainCard = activeCards[0];

    cards.forEach((card) => {
      const isRemoved = this.isCardRemoved(card);
      const isMainImage = !isRemoved && card === mainCard;
      const isFirstActive = !isRemoved && card === activeCards[0];
      const isLastActive = !isRemoved && card === activeCards[activeCards.length - 1];
      const mainBadge = this.mainBadgeForCard(card);
      const newBadge = this.newBadgeForCard(card);
      const dragHandle = this.dragHandleForCard(card);
      const removalBadge = this.removalBadgeForCard(card);
      const removeButton = this.removeButtonForCard(card);
      const reorderControls = this.reorderControlsForCard(card);
      const moveEarlierButton = this.moveEarlierButtonForCard(card);
      const moveLaterButton = this.moveLaterButtonForCard(card);

      card.dataset.preparationImageRemoved = isRemoved ? "true" : "false";
      card.classList.toggle("opacity-80", isRemoved);
      card.classList.toggle("bg-transparent", !isRemoved);
      card.classList.toggle("border-ec-border", !isRemoved);
      card.classList.toggle("border-ec-danger/30", isRemoved);
      card.classList.toggle("bg-status-neutral-bg", isRemoved);
      card.classList.toggle("ring-1", isRemoved);
      card.classList.toggle("ring-inset", isRemoved);
      card.classList.toggle("ring-red-100", isRemoved);

      if (mainBadge) {
        mainBadge.classList.toggle("hidden", !isMainImage);
        mainBadge.hidden = !isMainImage;
      }

      if (newBadge) {
        newBadge.classList.toggle("hidden", isMainImage);
        newBadge.hidden = isMainImage;
      }

      if (dragHandle) {
        dragHandle.classList.toggle("hidden", isRemoved);
        dragHandle.hidden = isRemoved;
      }

      if (removalBadge) {
        removalBadge.classList.toggle("hidden", !isRemoved);
        removalBadge.hidden = !isRemoved;
      }

      if (reorderControls) {
        reorderControls.classList.toggle("hidden", isRemoved);
      }

      this.toggleReorderButton(moveEarlierButton, isRemoved || isFirstActive);
      this.toggleReorderButton(moveLaterButton, isRemoved || isLastActive);

      if (removeButton) {
        const imageName = card.dataset.preparationImageFilename || "this photo";
        const removeLabel = removeButton.classList.contains("preparation-photo-card__remove-action")
          ? "Remove"
          : "×";
        removeButton.textContent = isRemoved ? "Undo" : removeLabel;
        removeButton.setAttribute("aria-pressed", isRemoved ? "true" : "false");
        removeButton.setAttribute(
          "aria-label",
          isRemoved ? `Undo removal of ${imageName}` : `Remove ${imageName}`
        );
        removeButton.classList.toggle("border-ec-danger/30", !isRemoved);
        removeButton.classList.toggle("text-ec-danger", !isRemoved);
        removeButton.classList.toggle("hover:bg-ec-danger/10", !isRemoved);
        removeButton.classList.toggle("border-ec-border", isRemoved);
        removeButton.classList.toggle("text-ec-text", isRemoved);
        removeButton.classList.toggle("hover:bg-ec-soft-surface", isRemoved);
      }
    });

    this.syncHiddenOrderInputs(cards);
    this.syncPresenceInput(cards);
  }

  moveCard(card, direction) {
    const movingClass = direction === "earlier"
      ? "preparation-photo-card--moving-earlier"
      : "preparation-photo-card--moving-later";

    const sibling = direction === "earlier"
      ? card.previousElementSibling
      : card.nextElementSibling;
    if (!sibling) return;

    card.classList.remove("preparation-photo-card--moving-earlier", "preparation-photo-card--moving-later");
    void card.offsetWidth;
    card.classList.add(movingClass);

    window.setTimeout(() => {
      if (direction === "earlier") {
        this.gridTarget.insertBefore(card, sibling);
      } else {
        this.gridTarget.insertBefore(sibling, card);
      }

      card.classList.remove("preparation-photo-card--moving-earlier", "preparation-photo-card--moving-later");
      this.refreshImageOrderState();
      this.dispatchGridChanged();
    }, 120);
  }

  isCardRemoved(card) {
    const removeInput = this.removeInputForCard(card);
    return removeInput ? !removeInput.disabled : false;
  }

  orderInputForCard(card) {
    return card.querySelector("[data-preparation-images-target='orderInput']");
  }

  removeInputForCard(card) {
    return card.querySelector("[data-preparation-images-target='removeInput']");
  }

  removeButtonForCard(card) {
    return card.querySelector("[data-preparation-images-target='removeButton']");
  }

  mainBadgeForCard(card) {
    return card.querySelector("[data-preparation-images-target='mainBadge']");
  }

  newBadgeForCard(card) {
    return card.querySelector("[data-preparation-images-target='newBadge']");
  }

  dragHandleForCard(card) {
    return card.querySelector("[data-preparation-images-target='dragHandle']");
  }

  removalBadgeForCard(card) {
    return card.querySelector("[data-preparation-images-target='removalBadge']");
  }

  reorderControlsForCard(card) {
    return card.querySelector("[data-preparation-images-target='reorderControls']");
  }

  moveEarlierButtonForCard(card) {
    return card.querySelector("[data-preparation-images-target='moveEarlierButton']");
  }

  moveLaterButtonForCard(card) {
    return card.querySelector("[data-preparation-images-target='moveLaterButton']");
  }

  toggleReorderButton(button, shouldDisable) {
    if (!button) return;

    button.disabled = shouldDisable;
    button.classList.toggle("opacity-50", shouldDisable);
    button.classList.toggle("cursor-not-allowed", shouldDisable);
    button.setAttribute("aria-disabled", shouldDisable ? "true" : "false");
  }

  syncHiddenOrderInputs(cards) {
    cards.forEach((card) => {
      const input = this.orderInputForCard(card);
      if (!input) return;

      input.value = card.dataset.preparationImageOrderToken || card.dataset.preparationImageId || input.value;
    });
  }

  syncPresenceInput(cards) {
    if (!this.hasPresenceInputTarget) return;

    const activeCards = cards.filter((card) => !this.isCardRemoved(card));
    this.presenceInputTarget.value = activeCards.length > 0 ? "1" : "";
  }

  dispatchGridChanged() {
    this.gridTarget.dispatchEvent(new CustomEvent("preparation-images:changed", {
      bubbles: true
    }));
  }
}
